import { useEffect, useMemo, useState } from "react";

const pad = (n) => String(n).padStart(2, "0");

/**
 * Elegant, luxury countdown timer.
 * Props:
 *  - endTime: Date | string | number — the target end time
 *  - tone: 'gold' | 'dark' | 'light'
 *  - size: 'sm' | 'md' | 'lg'
 *  - showLabels: bool
 *  - testId: string for automation
 *
 * Perf: one setInterval(1000). No layout thrashing. Uses tabular-nums so
 * digits don't jitter. Pauses automatically when reduced-motion is set.
 */
const CountdownTimer = ({
  endTime,
  tone = "gold",
  size = "md",
  showLabels = true,
  testId,
  className = "",
}) => {
  const target = useMemo(() => new Date(endTime).getTime(), [endTime]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    // Align to the next full second so digits flip cleanly
    const align = 1000 - (Date.now() % 1000);
    let id;
    const to = setTimeout(() => {
      setNow(Date.now());
      id = setInterval(() => setNow(Date.now()), 1000);
    }, align);
    return () => {
      clearTimeout(to);
      if (id) clearInterval(id);
    };
  }, []);

  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  const chipCls = {
    sm: "text-[13px] h-8 min-w-[36px] px-1.5",
    md: "text-lg h-11 min-w-[44px] px-2",
    lg: "text-3xl sm:text-4xl h-16 min-w-[64px] sm:min-w-[72px] px-3",
  }[size];

  const toneCls =
    tone === "dark"
      ? "bg-white/[0.06] text-white border border-white/15"
      : tone === "light"
      ? "bg-white text-black border border-gray-200"
      : "bg-[#D4AF37] text-black";

  const labelCls =
    tone === "dark"
      ? "text-white/50"
      : tone === "light"
      ? "text-gray-500"
      : "text-black/70";

  const sepCls =
    tone === "dark" ? "text-white/30" : tone === "light" ? "text-gray-300" : "text-black/40";

  const Chip = ({ v, label }) => (
    <div className="flex flex-col items-center gap-1">
      <div
        className={`${chipCls} ${toneCls} flex items-center justify-center font-heading font-semibold tabular-nums`}
      >
        {pad(v)}
      </div>
      {showLabels && (
        <span className={`text-[9px] uppercase tracking-[0.25em] ${labelCls}`}>{label}</span>
      )}
    </div>
  );

  const Sep = () => (
    <span
      className={`self-start ${
        size === "lg" ? "pt-4 text-3xl sm:text-4xl" : size === "md" ? "pt-1 text-lg" : "pt-1 text-[13px]"
      } font-heading font-semibold ${sepCls}`}
    >
      :
    </span>
  );

  const showDays = days > 0;
  const expired = diff === 0;

  return (
    <div
      data-testid={testId}
      aria-live="off"
      className={`flex items-center gap-1.5 ${expired ? "opacity-70" : ""} ${className}`}
    >
      {showDays && (
        <>
          <Chip v={days} label="Days" />
          <Sep />
        </>
      )}
      <Chip v={hours} label="Hours" />
      <Sep />
      <Chip v={minutes} label="Mins" />
      <Sep />
      <Chip v={seconds} label="Secs" />
    </div>
  );
};

export default CountdownTimer;
